// Shared "orders just created" panel for every PSP's Backfill dialog. Lists each created order (number,
// date, amount) and offers a per-order "Email receipt" button. Receipts are NEVER sent automatically —
// one explicit click per order, so a bulk back-dated backfill can't blast a donor with historical
// receipts. The send funnels to the same manual admin endpoint (/api/orders/:n/resend-receipt), which
// itself refuses internal / no-email addresses and non-completed orders; those come back as a per-row error.
import React, { useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { fmtMoney, fmtDate } from './reconShared';
import { post } from '../../utils/api';

// orders: [{ order_number, amount, date?, note? }]  — rows without an order_number are skipped.
export default function CreatedOrderReceipts({ orders }) {
  const rows = (orders || []).filter((o) => o && o.order_number);
  const [state, setState] = useState({}); // order_number -> { status: 'sending'|'sent'|'error', msg? }

  const sendReceipt = async (orderNumber) => {
    setState((p) => ({ ...p, [orderNumber]: { status: 'sending' } }));
    try {
      await post(`/api/orders/${encodeURIComponent(orderNumber)}/resend-receipt`, {});
      setState((p) => ({ ...p, [orderNumber]: { status: 'sent' } }));
    } catch (e) {
      setState((p) => ({ ...p, [orderNumber]: { status: 'error', msg: e?.response?.data?.error || e?.message || 'Send failed' } }));
    }
  };

  if (!rows.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Orders created</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Receipt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((o) => {
            const st = state[o.order_number] || {};
            return (
              <TableRow key={o.order_number}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>#{o.order_number}</Typography>
                  {o.note && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.note}</Typography>}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{o.date ? fmtDate(o.date) : '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(o.amount)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {st.status === 'sent' ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                      <CheckCircleIcon fontSize="small" /><Typography variant="caption">Sent</Typography>
                    </Box>
                  ) : st.status === 'error' ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'error.main' }} title={st.msg}>
                      <ErrorOutlineIcon fontSize="small" />
                      <Button size="small" onClick={() => sendReceipt(o.order_number)}>Retry</Button>
                    </Box>
                  ) : (
                    <Button size="small" variant="outlined" disabled={st.status === 'sending'} onClick={() => sendReceipt(o.order_number)}>
                      {st.status === 'sending' ? <CircularProgress size={14} /> : 'Email receipt'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {Object.values(state).some((s) => s.status === 'error') && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
          {Object.entries(state).filter(([, s]) => s.status === 'error').map(([n, s]) => `#${n}: ${s.msg}`).join(' · ')}
        </Typography>
      )}
    </Box>
  );
}
